import { TFunction } from "i18next";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import useClipboard from "react-use-clipboard";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getLeagueForEntry } from "core-integration/src/store/entries/reducers";
import { fetchEntrySummary } from "core-integration/src/store/entries/thunks";
import { getCode } from "core-integration/src/store/leagues/reducers";
import { fetchLeagueCode } from "core-integration/src/store/leagues/thunks";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { ILoggedInPlayer } from "core-integration/src/store/player/types";
import Button from "../Button";
import Copy from "../Copy";
import { ControlArrowRight } from "../icons/Arrows";
import { Main, Wrapper } from "../Layout";
import Title from "../Title";

const LeagueInvite = styled.div`
  margin-bottom: ${({ theme }) => theme.space[4]};
  padding: ${({ theme }) => theme.space[2]};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.space[3]};
  text-align: center;
`;

const LeagueInviteCode = styled.strong`
  margin-left: ${({ theme }) => theme.space[1]};
  color: ${({ theme }) => theme.colors.yellow};
`;

const Hr = styled.hr`
  border: 0;
  height: 1px;
  margin-top: 23px;
  margin-bottom: 23px;
  background-color: ${({ theme }) => theme.colors.grey};
`;

const inviteMessage = (code: string, leagueName: string, t: TFunction) => [
  t("invite.inviteMsg1", "Hi"),
  t("invite.inviteMsg2", `I've created a league called ${leagueName}.`, {
    leagueName,
  }),
  `${window.location.origin}/leagues/auto-join/${code}`,
  t(
    "invite.inviteMsg3",
    `You shouldn't need the league code, but just in case here it is: ${code}`,
    {
      code,
    },
  ),
  t("invite.inviteMsg4", "Enjoy the game!"),
];

const Invite: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch>();
  const { leagueId } = useParams();

  const player = useSelector(getPlayerData) as ILoggedInPlayer; // enforced by EntryRoute
  const entryId = player.entry;
  const leagueNumber =
    leagueId && parseInt(leagueId, 10) ? parseInt(leagueId, 10) : 0;
  const code = useSelector((state: RootState) =>
    leagueNumber ? getCode(state, leagueNumber) : null,
  );
  const league = useSelector((state: RootState) =>
    leagueNumber ? getLeagueForEntry(state, entryId, leagueNumber) : null,
  );

  const codeToCopy = code ? code : "";
  const leagueName = league ? league.name : "";
  const message = inviteMessage(codeToCopy, leagueName, t);
  const emailText = message ? message.join("\n\n") : "";
  const emailBody = encodeURIComponent(emailText);
  const emailSubject = t("invite.emailSubject", "Play Allsvenskan Fantasy");
  const encodedEmailSubject = encodeURI(emailSubject);
  const joinUrl = `${window.location.origin}/leagues/auto-join/${code}`;
  const [isCopied, setCopied] = useClipboard(codeToCopy, {
    successDuration: 500,
  });
  const [isCopiedUrl, setCopiedUrl] = useClipboard(joinUrl, {
    successDuration: 500,
  });
  const [isCopiedEmail, setCopiedEmail] = useClipboard(emailText, {
    successDuration: 500,
  });

  useEffect(() => {
    if (leagueNumber) {
      dispatch(fetchLeagueCode(leagueNumber));
    }

    dispatch(fetchEntrySummary(entryId));
  }, [dispatch, entryId, leagueNumber]);

  if (!codeToCopy || !leagueNumber || !league) {
    return null;
  }

  return (
    <Wrapper>
      <Main>
        <Copy>
          <Title>
            {t("invite.title", "Invite players to join")} {leagueName}
          </Title>
          <LeagueInvite onDoubleClick={setCopied}>
            {t("invite.codeToJoin", "Code to join this league")}:
            <LeagueInviteCode>
              {isCopied ? t("invite.copied", "Copied!") : codeToCopy}
            </LeagueInviteCode>
          </LeagueInvite>

          <Button onClick={setCopiedUrl}>
            {isCopiedUrl
              ? t("invite.copied", "Copied!")
              : t("invite.copyUrl", "Copy auto-join link")}
          </Button>

          <Hr />

          <p>
            {t(
              "invite.description1",
              "When you click the link below, the link will attempt to open your default email program and start a new email.",
            )}
          </p>

          <p>
            {t(
              "invite.description2",
              "If you do not have a default email program, like Outlook or Mail, enabled on your computer, clicking the link may not result in any action or you may be prompted to set up an email program.",
            )}
          </p>

          <Button
            onClick={() =>
              (window.location.href = `mailto:?to=&subject=${encodedEmailSubject}&body=${emailBody}`)
            }
          >
            {t("invite.byEmailButton", "Share league code by email")}{" "}
            <ControlArrowRight />
          </Button>

          <Hr />

          <p>
            {t(
              "invite.description3",
              "If you use a browser based webmail application, like Gmail or Yahoo!, you can copy and paste the text below:",
            )}
          </p>

          <Box bg="primary" p={2} mb={3} onDoubleClick={setCopiedEmail}>
            {message.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </Box>

          <Button onClick={setCopiedEmail}>
            {isCopiedEmail
              ? t("invite.copied", "Copied!")
              : t("invite.copyText", "Copy email text")}
          </Button>
        </Copy>
      </Main>
    </Wrapper>
  );
};

export default Invite;
